import React, { useState } from "react";
import api, { formatApiError } from "@/lib/api";
import { X, Check, Copy, UserPlus, AlertCircle, FileText, Zap, FileSignature } from "lucide-react";

const NZ_REGIONS = [
  "Northland", "Auckland", "Waikato", "Bay of Plenty", "Gisborne", "Hawke's Bay",
  "Taranaki", "Manawatū-Whanganui", "Wellington", "Tasman", "Nelson", "Marlborough",
  "West Coast", "Canterbury", "Otago", "Southland",
];

const OWNERSHIP_OPTIONS = [
  { v: "", label: " " },
  { v: "owned", label: "Owned" },
  { v: "leased", label: "Leased" },
  { v: "shared", label: "Shared" },
  { v: "other", label: "Other" },
];

const EMPTY = {
  name: "", email: "", phone: "",
  farm_name: "", region: "", town_city: "", address_line: "", postcode: "",
  ownership: "", years_growing: 0, dahlia_years: 0, growing_area_sqm: 0,
  target_subscribers: 0, weekly_capacity_boxes: 0,
  why_join: "", vision_statement: "",
  founding_grower: false, payment_split_pct: "", start_date: "",
  admin_notes: "",
};

export default function ManualGrowerModal({ onClose, onCreated }) {
  const [mode, setMode] = useState("application"); // application | pre_approved | direct
  const [form, setForm] = useState(EMPTY);
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState("");
  const [result, setResult] = useState(null);
  const [copied, setCopied] = useState(false);

  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  const validate = () => {
    if (form.name.trim().length < 2 || !form.email.includes("@")) {
      return "Please give a name and a valid email.";
    }
    if (mode === "direct" || mode === "pre_approved") {
      if (!form.farm_name.trim()) return "Farm name is required.";
      if (!form.region.trim()) return "Region is required.";
    }
    return "";
  };

  const submit = async (e) => {
    e.preventDefault();
    setErr("");
    const v = validate();
    if (v) return setErr(v);
    setBusy(true);
    try {
      let endpoint;
      let payload;
      if (mode === "direct") {
        endpoint = "/admin/growers/manual-direct";
        payload = {
          name: form.name, email: form.email, phone: form.phone || "",
          farm_name: form.farm_name, region: form.region,
          town_city: form.town_city || "", address_line: form.address_line || "",
          postcode: form.postcode || "",
          founding_grower: !!form.founding_grower,
          weekly_capacity_boxes: Number(form.weekly_capacity_boxes) || 0,
          payment_split_pct: form.payment_split_pct === "" ? null : Number(form.payment_split_pct),
          start_date: form.start_date || "",
          admin_notes: form.admin_notes || "",
        };
      } else if (mode === "pre_approved") {
        endpoint = "/admin/growers/manual-pre-approved";
        payload = {
          name: form.name, email: form.email, phone: form.phone || "",
          farm_name: form.farm_name, region: form.region,
          town_city: form.town_city || "", address_line: form.address_line || "",
          postcode: form.postcode || "",
          founding_grower: !!form.founding_grower,
          weekly_capacity_boxes: Number(form.weekly_capacity_boxes) || 0,
          payment_split_pct: form.payment_split_pct === "" ? null : Number(form.payment_split_pct),
          start_date: form.start_date || "",
          admin_notes: form.admin_notes || "",
        };
      } else {
        endpoint = "/admin/growers/manual-application";
        payload = {
          name: form.name, email: form.email, phone: form.phone || "",
          farm_name: form.farm_name || "", region: form.region || "",
          town_city: form.town_city || "", address_line: form.address_line || "",
          postcode: form.postcode || "",
          ownership: form.ownership || "",
          years_growing: Number(form.years_growing) || 0,
          dahlia_years: Number(form.dahlia_years) || 0,
          growing_area_sqm: Number(form.growing_area_sqm) || 0,
          target_subscribers: Number(form.target_subscribers) || 0,
          weekly_capacity_boxes: Number(form.weekly_capacity_boxes) || 0,
          why_join: form.why_join || "",
          vision_statement: form.vision_statement || "",
          admin_notes: form.admin_notes || "",
        };
      }
      const { data } = await api.post(endpoint, payload);
      setResult({ ...data, mode });
      onCreated?.(data);
    } catch (e2) {
      setErr(formatApiError(e2.response?.data?.detail));
    } finally {
      setBusy(false);
    }
  };

  const copy = () => {
    navigator.clipboard.writeText(result.temp_password);
    setCopied(true);
    setTimeout(() => setCopied(false), 1500);
  };

  return (
    <div className="fixed inset-0 z-50 bg-[var(--bloom-ink)]/60 backdrop-blur-sm flex items-center justify-center p-4 overflow-y-auto" onClick={onClose} data-testid="manual-grower-modal">
      <div className="bg-[var(--bloom-bg)] w-full max-w-2xl my-8" onClick={(e) => e.stopPropagation()}>
        <div className="flex items-center justify-between p-5 border-b border-[var(--bloom-border)]">
          <div className="flex items-center gap-2">
            <UserPlus size={16} strokeWidth={1.5} />
            <h2 className="font-serif text-2xl">Add a Grower manually</h2>
          </div>
          <button onClick={onClose} className="p-1.5 hover:bg-[var(--bloom-surface)]" data-testid="manual-grower-close"><X size={16} /></button>
        </div>

        {!result ? (
          <form onSubmit={submit} className="p-6" data-testid="manual-grower-form">
            {/* Mode toggle */}
            <div className="grid grid-cols-1 md:grid-cols-3 gap-3 mb-6">
              <ModeCard
                active={mode === "application"}
                onClick={() => setMode("application")}
                icon={<FileText size={18} strokeWidth={1.5} />}
                title="Submit Application"
                hint="Pre-fill the application on the grower's behalf. They sign in and finish the wizard, then go through review."
                testId="manual-mode-application"
              />
              <ModeCard
                active={mode === "pre_approved"}
                onClick={() => setMode("pre_approved")}
                icon={<FileSignature size={18} strokeWidth={1.5} />}
                title="Pre-approved (Docs only)"
                hint="Already approved. They just need to sign in, read the documents, and sign the agreement. Then auto-active."
                testId="manual-mode-pre-approved"
              />
              <ModeCard
                active={mode === "direct"}
                onClick={() => setMode("direct")}
                icon={<Zap size={18} strokeWidth={1.5} />}
                title="Bypass &amp; Add Directly"
                hint="Skip everything. Grower goes straight to the portal as Active. No docs, no signing."
                testId="manual-mode-direct"
              />
            </div>

            <div className="space-y-5">
              <Section title="Contact">
                <Grid cols={2}>
                  <Field label="Full name *"><input value={form.name} onChange={(e) => set("name", e.target.value)} className="bloom-input" autoFocus data-testid="manual-grower-name" /></Field>
                  <Field label="Email *"><input type="email" value={form.email} onChange={(e) => set("email", e.target.value)} className="bloom-input" data-testid="manual-grower-email" /></Field>
                  <Field label="Phone"><input value={form.phone} onChange={(e) => set("phone", e.target.value)} className="bloom-input" data-testid="manual-grower-phone" /></Field>
                  <Field label={mode === "direct" ? "Farm name *" : "Farm name"}>
                    <input value={form.farm_name} onChange={(e) => set("farm_name", e.target.value)} className="bloom-input" data-testid="manual-grower-farm" />
                  </Field>
                </Grid>
              </Section>

              <Section title="Location">
                <Grid cols={2}>
                  <Field label={mode === "application" ? "Region" : "Region *"}>
                    <select value={form.region} onChange={(e) => set("region", e.target.value)} className="bloom-input" data-testid="manual-grower-region">
                      <option value=""> </option>
                      {NZ_REGIONS.map((r) => <option key={r} value={r}>{r}</option>)}
                    </select>
                  </Field>
                  <Field label="Town / City"><input value={form.town_city} onChange={(e) => set("town_city", e.target.value)} className="bloom-input" data-testid="manual-grower-town" /></Field>
                  <Field label="Address line"><input value={form.address_line} onChange={(e) => set("address_line", e.target.value)} className="bloom-input" /></Field>
                  <Field label="Postcode"><input value={form.postcode} onChange={(e) => set("postcode", e.target.value)} className="bloom-input" /></Field>
                </Grid>
              </Section>

              {mode === "application" ? (
                <>
                  <Section title="Experience &amp; capacity">
                    <Grid cols={2}>
                      <Field label="Ownership">
                        <select value={form.ownership} onChange={(e) => set("ownership", e.target.value)} className="bloom-input">
                          {OWNERSHIP_OPTIONS.map((o) => <option key={o.v} value={o.v}>{o.label}</option>)}
                        </select>
                      </Field>
                      <Field label="Years growing"><input type="number" min="0" value={form.years_growing} onChange={(e) => set("years_growing", e.target.value)} className="bloom-input" /></Field>
                      <Field label="Years with dahlias"><input type="number" min="0" value={form.dahlia_years} onChange={(e) => set("dahlia_years", e.target.value)} className="bloom-input" /></Field>
                      <Field label="Growing area (sqm)"><input type="number" min="0" value={form.growing_area_sqm} onChange={(e) => set("growing_area_sqm", e.target.value)} className="bloom-input" /></Field>
                      <Field label="Target subscribers"><input type="number" min="0" value={form.target_subscribers} onChange={(e) => set("target_subscribers", e.target.value)} className="bloom-input" /></Field>
                      <Field label="Weekly capacity (boxes)"><input type="number" min="0" value={form.weekly_capacity_boxes} onChange={(e) => set("weekly_capacity_boxes", e.target.value)} className="bloom-input" /></Field>
                    </Grid>
                  </Section>
                  <Section title="Story">
                    <Field label="Why join the Club"><textarea rows={2} value={form.why_join} onChange={(e) => set("why_join", e.target.value)} className="bloom-input" /></Field>
                    <Field label="Vision statement"><textarea rows={2} value={form.vision_statement} onChange={(e) => set("vision_statement", e.target.value)} className="bloom-input" /></Field>
                  </Section>
                </>
              ) : (
                <Section title="Bloom Club role">
                  <Grid cols={2}>
                    <Field label="Founding Grower">
                      <label className="inline-flex items-center gap-2 text-sm">
                        <input type="checkbox" checked={!!form.founding_grower} onChange={(e) => set("founding_grower", e.target.checked)} data-testid="manual-grower-founding" />
                        <span className="font-serif italic">Mark as Founding Grower</span>
                      </label>
                    </Field>
                    <Field label="Weekly capacity (boxes)"><input type="number" min="0" value={form.weekly_capacity_boxes} onChange={(e) => set("weekly_capacity_boxes", e.target.value)} className="bloom-input" data-testid="manual-grower-capacity" /></Field>
                    <Field label="Payment split (% to grower)">
                      <input type="number" min="0" max="100" placeholder="e.g. 70" value={form.payment_split_pct} onChange={(e) => set("payment_split_pct", e.target.value)} className="bloom-input" data-testid="manual-grower-split" />
                    </Field>
                    <Field label="Start date"><input type="date" value={form.start_date} onChange={(e) => set("start_date", e.target.value)} className="bloom-input" data-testid="manual-grower-startdate" /></Field>
                  </Grid>
                </Section>
              )}

              <Section title="Admin notes">
                <Field label="Internal notes (only visible to admins)">
                  <textarea rows={2} value={form.admin_notes} onChange={(e) => set("admin_notes", e.target.value)} className="bloom-input" data-testid="manual-grower-notes" />
                </Field>
              </Section>
            </div>

            {err && <div className="mt-4 flex items-center gap-2 text-sm text-[var(--bloom-accent)] font-serif italic" data-testid="manual-grower-error"><AlertCircle size={14} /> {err}</div>}

            <div className="mt-6 flex justify-end gap-3">
              <button type="button" onClick={onClose} className="bloom-btn bloom-btn-ghost">Cancel</button>
              <button type="submit" disabled={busy} className="bloom-btn bloom-btn-primary" data-testid="manual-grower-submit">
                {busy ? "Creating…" :
                  mode === "direct" ? "Add as Active Grower" :
                  mode === "pre_approved" ? "Create Pre-approved Grower" :
                  "Create Application"}
              </button>
            </div>
          </form>
        ) : (
          <div className="p-8" data-testid="manual-grower-success">
            <Check className="mx-auto mb-3 text-[var(--bloom-ink)]" size={36} strokeWidth={1.5} />
            <h3 className="font-serif text-2xl text-center mb-1">{result.user.name} added.</h3>
            <p className="text-center font-serif italic text-[var(--bloom-ink-soft)] mb-4">{result.user.email}</p>
            <div className="text-center mb-6">
              <span className="inline-block text-[10px] tracking-[0.2em] uppercase px-3 py-1 border border-[var(--bloom-border)] bg-[var(--bloom-surface)]" data-testid="manual-grower-status-pill">
                {result.mode === "direct" ? "Active Grower" :
                 result.mode === "pre_approved" ? "Pre-approved · Awaiting Document Signing" :
                 "Application Created by Admin / Awaiting Grower Review"}
              </span>
            </div>

            <div className="border border-[var(--bloom-border)] bg-[var(--bloom-surface)] p-5">
              <div className="bloom-label mb-2">Temporary password</div>
              <div className="flex items-center justify-between gap-3">
                <code className="font-mono text-base" data-testid="manual-grower-temp-password">{result.temp_password}</code>
                <button onClick={copy} className="bloom-btn bloom-btn-outline text-xs"><Copy size={12} /> {copied ? "Copied" : "Copy"}</button>
              </div>
              <p className="text-xs italic text-[var(--bloom-ink-muted)] mt-3 font-serif">
                Share this with the grower so they can sign in at <strong>/login</strong>. They&rsquo;ll be asked to change it on first sign-in.
              </p>
            </div>

            <div className="mt-6 text-center">
              <button onClick={onClose} className="bloom-btn bloom-btn-primary" data-testid="manual-grower-done">Done</button>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}

function ModeCard({ active, onClick, icon, title, hint, testId }) {
  return (
    <button type="button" onClick={onClick} className={`text-left p-4 border transition ${active ? "border-[var(--bloom-ink)] bg-[var(--bloom-surface)]" : "border-[var(--bloom-border)] bg-white/40 hover:bg-white/70"}`} data-testid={testId}>
      <div className="flex items-center gap-2 mb-2">{icon}<div className="font-serif text-lg">{title}</div></div>
      <div className="font-serif italic text-sm text-[var(--bloom-ink-soft)] leading-snug" dangerouslySetInnerHTML={{ __html: hint }} />
    </button>
  );
}

function Section({ title, children }) {
  return (
    <div>
      <div className="text-[10px] tracking-[0.25em] uppercase text-[var(--bloom-ink-muted)] mb-3">{title}</div>
      <div className="space-y-4">{children}</div>
    </div>
  );
}

function Grid({ cols = 2, children }) {
  return <div className={`grid grid-cols-1 md:grid-cols-${cols} gap-4`}>{children}</div>;
}

function Field({ label, children }) {
  return (
    <div>
      <div className="bloom-label mb-2">{label}</div>
      {children}
    </div>
  );
}
